import { useState, useEffect } from "react";
import axios from "axios";
import "./AliExpressOrderHistory.css";

const AliExpressOrderHistory = ({ onViewOrder }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, processing, shipped, delivered, cancelled

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/aliexpress/orders`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setOrders(res.data.orders || []);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar pedidos:", error);
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      processing: "#ff9800",
      confirmed: "#2196f3",
      shipped: "#9c27b0",
      delivered: "#4caf50",
      cancelled: "#f44336",
    };
    return colors[status] || "#666";
  };

  const getStatusText = (status) => {
    const texts = {
      processing: "Processando",
      confirmed: "Confirmado",
      shipped: "Enviado",
      delivered: "Entregue",
      cancelled: "Cancelado",
    };
    return texts[status] || status;
  };

  const getPaymentStatusText = (status) => {
    const texts = {
      pending: "Pendente",
      waiting: "Aguardando",
      paid: "Pago",
      failed: "Falhou",
      refunded: "Reembolsado",
    };
    return texts[status] || status;
  };

  const filteredOrders = filter === "all" 
    ? orders 
    : orders.filter(order => order.status === filter);

  if (loading) {
    return (
      <div className="order-history-loading">
        <div className="spinner"></div>
        <p>Carregando pedidos...</p>
      </div>
    );
  }

  return (
    <div className="order-history-container">
      <div className="order-history-header">
        <h2>Meus Pedidos</h2>
        <p className="order-count">{orders.length} pedido(s)</p>
      </div>

      {/* Filter Tabs */}
      <div className="order-filter-tabs">
        <button
          className={`filter-tab ${filter === "all" ? "active" : ""}`}
          onClick={() => setFilter("all")}
        >
          <span>Todos</span>
          <span className="tab-count">{orders.length}</span>
        </button>
        <button
          className={`filter-tab ${filter === "processing" ? "active" : ""}`}
          onClick={() => setFilter("processing")}
        >
          <span>Processando</span>
          <span className="tab-count">
            {orders.filter(o => o.status === "processing").length}
          </span>
        </button>
        <button
          className={`filter-tab ${filter === "shipped" ? "active" : ""}`}
          onClick={() => setFilter("shipped")}
        >
          <span>Enviados</span>
          <span className="tab-count">
            {orders.filter(o => o.status === "shipped").length}
          </span>
        </button>
        <button
          className={`filter-tab ${filter === "delivered" ? "active" : ""}`}
          onClick={() => setFilter("delivered")}
        >
          <span>Entregues</span>
          <span className="tab-count">
            {orders.filter(o => o.status === "delivered").length}
          </span>
        </button>
      </div>

      {/* Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="no-orders">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 12 7.4l3.38 4.6L17 10.83 14.92 8H20v6z"/>
          </svg>
          <h3>Nenhum pedido encontrado</h3>
          <p>Seus pedidos aparecerão aqui</p>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map((order) => (
            <div key={order._id} className="order-card">
              <div className="order-header">
                <div className="order-id">
                  <strong>Pedido #{order._id.slice(-8)}</strong>
                  <span className="order-date">
                    {new Date(order.createdAt).toLocaleDateString("pt-BR")}
                  </span>
                </div>
                <div 
                  className="order-status-badge"
                  style={{ backgroundColor: getStatusColor(order.status) }}
                >
                  {getStatusText(order.status)}
                </div>
              </div>

              <div className="order-items">
                {order.items.slice(0, 3).map((item, idx) => (
                  <div key={idx} className="order-item-mini">
                    <img 
                      src={item.image || item.product?.image} 
                      alt={item.name} 
                    />
                    <div className="order-item-info">
                      <p className="item-name">{item.name}</p>
                      <p className="item-quantity">Qtd: {item.quantity}</p>
                    </div>
                    <span className="item-price">
                      R$ {(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                {order.items.length > 3 && (
                  <p className="more-items">+{order.items.length - 3} itens</p>
                )}
              </div>

              <div className="order-footer">
                <div className="order-totals">
                  <div className="total-row">
                    <span>Subtotal:</span>
                    <span>R$ {order.subtotal.toFixed(2)}</span>
                  </div>
                  {order.shipping > 0 && (
                    <div className="total-row">
                      <span>Frete:</span>
                      <span>R$ {order.shipping.toFixed(2)}</span>
                    </div>
                  )}
                  {order.discount > 0 && (
                    <div className="total-row discount">
                      <span>Desconto:</span>
                      <span>-R$ {order.discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="total-row total">
                    <span>Total:</span>
                    <span>R$ {order.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="order-actions">
                  <button 
                    className="btn-view-order"
                    onClick={() => onViewOrder && onViewOrder(order)}
                  >
                    Ver Detalhes
                  </button>
                  {order.status === "shipped" && order.trackingNumber && (
                    <button className="btn-track-order">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                      </svg>
                      Rastrear
                    </button>
                  )}
                  {order.status === "delivered" && (
                    <button className="btn-review-order">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                      </svg>
                      Avaliar
                    </button>
                  )}
                </div>
              </div>

              {/* Payment Status */}
              <div className="order-payment-info">
                <span className="payment-method">
                  Pagamento: {order.paymentMethod === "card" ? "Cartão de Crédito" : 
                              order.paymentMethod === "pix" ? "PIX" : "Boleto"}
                </span>
                <span className={`payment-status payment-${order.paymentStatus}`}>
                  {getPaymentStatusText(order.paymentStatus)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AliExpressOrderHistory;
